// src/pages/RentaCrear.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 📝 CREAR NUEVA RENTA - SISTEMA RENTACAR eFIT
// Formulario profesional para registro de contratos de alquiler
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const descargarContratoPDF = async (rentaId, numero_contrato) => {
  try {
    const res = await api.get(`/rencar/rentas/${rentaId}/contrato`, { responseType: 'blob' });
    const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `contrato-${numero_contrato || 'RC-' + String(rentaId).padStart(4, '0')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo descargar el contrato PDF', confirmButtonColor: '#3b82f6' });
  }
};

const RentaCrear = () => {
  const navigate = useNavigate();
  const [vehiculos, setVehiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculando, setCalculando] = useState(false);

  const [form, setForm] = useState({
    cliente_id: "",
    vehiculo_id: "",
    fecha_salida: "",
    fecha_retorno: "",
    deposito: "",
    observaciones: "",
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 CARGAR DATOS INICIALES
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [resVeh, resCli] = await Promise.all([
        api.get("/rencar/vehiculos"),
        api.get("/clientes")
      ]);
      
      // Solo vehículos disponibles
      const disponibles = (resVeh.data.vehiculos || []).filter(v => v.estado === 'disponible');
      setVehiculos(disponibles);
      setClientes(resCli.data.clientes || resCli.data || []);
    } catch (error) {
      console.error("❌ Error cargando datos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los datos necesarios",
        confirmButtonColor: "#3b82f6"
      });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 📊 CALCULAR DATOS AUTOMÁTICOS
  // ═══════════════════════════════════════════════════════════════════════════
  const vehiculoSeleccionado = vehiculos.find(v => v.id === parseInt(form.vehiculo_id));
  const precioDialo = vehiculoSeleccionado?.costo_diario || 0;
  
  const calcularDias = () => {
    if (!form.fecha_salida || !form.fecha_retorno) return 0;
    const salida = new Date(form.fecha_salida);
    const retorno = new Date(form.fecha_retorno);
    const diff = retorno.getTime() - salida.getTime();
    const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return dias > 0 ? dias : 0;
  };

  const dias = calcularDias();
  const subtotal = dias * precioDialo;
  const deposito = parseFloat(form.deposito) || 0;
  const totalPagar = subtotal + deposito;

  // ═══════════════════════════════════════════════════════════════════════════
  // 💾 CREAR RENTA
  // ═══════════════════════════════════════════════════════════════════════════
  const crearRenta = async () => {
    // Validaciones
    if (!form.cliente_id || !form.vehiculo_id || !form.fecha_salida || !form.fecha_retorno) {
      return Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Por favor completa todos los campos obligatorios",
        confirmButtonColor: "#3b82f6"
      });
    }

    if (dias <= 0) {
      return Swal.fire({
        icon: "error",
        title: "Fechas inválidas",
        text: "La fecha de retorno debe ser posterior a la fecha de salida",
        confirmButtonColor: "#3b82f6"
      });
    }

    const payload = {
      cliente_id: parseInt(form.cliente_id),
      vehiculo_id: parseInt(form.vehiculo_id),
      fecha_salida: form.fecha_salida,
      fecha_retorno_programada: form.fecha_retorno,
      deposito: deposito,
      observaciones: form.observaciones
    };

    setLoading(true);
    try {
      const response = await api.post("/rencar/rentas", payload);
      
      if (response.data.ok) {
        const nuevaRentaId = response.data.renta.id;

        Swal.fire({
          icon: "success",
          title: "✅ Contrato Creado",
          html: `
            <div style="text-align: left; padding: 10px;">
              <p><strong>Renta #${nuevaRentaId}</strong></p>
              <p>Vehículo: ${vehiculoSeleccionado.marca} ${vehiculoSeleccionado.modelo}</p>
              <p>Cliente: ${clientes.find(c => c.id === parseInt(form.cliente_id))?.nombre}</p>
              <p style="font-size: 18px; color: #10b981; margin-top: 12px;">
                <strong>Total: USD $${totalPagar.toLocaleString('en-US', {minimumFractionDigits:2})}</strong>
              </p>
            </div>
          `,
          showDenyButton: true,
          confirmButtonText: "📸 Hacer Inspección de Salida",
          denyButtonText: "📄 Descargar Contrato",
          confirmButtonColor: "#3b82f6",
          denyButtonColor: "#6366f1",
          allowOutsideClick: false
        }).then((result) => {
          if (result.isDenied) {
            descargarContratoPDF(nuevaRentaId, response.data.renta?.numero_contrato);
            setTimeout(() => navigate(`/rencar/inspeccion-salida/${nuevaRentaId}`), 1200);
          } else if (result.isConfirmed) {
            navigate(`/rencar/inspeccion-salida/${nuevaRentaId}`);
          }
        });
      }
    } catch (error) {
      console.error("❌ Error creando renta:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.msg || "No se pudo crear el contrato",
        confirmButtonColor: "#3b82f6"
      });
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.pageTitle}>
          <span style={styles.pageTitleIcon}>📝</span>
          <div>
            <h1 style={styles.pageTitleText}>Nuevo Contrato de Renta</h1>
            <p style={styles.pageTitleSub}>
              Complete los datos para generar un nuevo contrato de alquiler
            </p>
          </div>
        </div>
        <div style={styles.pageStats}>
          <div style={styles.statBadge}>
            🚗 {vehiculos.length} Disponibles
          </div>
        </div>
      </div>

      {/* Formulario Principal */}
      <div style={styles.formCard}>
        {/* Sección 1: Cliente y Vehículo */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>👤 Información del Contrato</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Cliente *</label>
              <select
                style={styles.input}
                value={form.cliente_id}
                onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
              >
                <option value="">Seleccione un cliente</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.nombre} - {c.rnc_cedula}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Vehículo *</label>
              <select
                style={styles.input}
                value={form.vehiculo_id}
                onChange={(e) => setForm({ ...form, vehiculo_id: e.target.value })}
              >
                <option value="">Seleccione un vehículo</option>
                {vehiculos.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.marca} {v.modelo} ({v.placa}) — USD ${parseFloat(v.costo_diario).toLocaleString('en-US', {minimumFractionDigits:2})}/día
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Sección 2: Fechas */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📅 Periodo de Alquiler</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Fecha de Salida *</label>
              <input
                type="date"
                style={styles.input}
                value={form.fecha_salida}
                onChange={(e) => setForm({ ...form, fecha_salida: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Fecha de Retorno *</label>
              <input
                type="date"
                style={styles.input}
                value={form.fecha_retorno}
                onChange={(e) => setForm({ ...form, fecha_retorno: e.target.value })}
                min={form.fecha_salida || new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          {/* Cálculo Automático */}
          {dias > 0 && (
            <div style={styles.calculoCard}>
              <div style={styles.calculoRow}>
                <span style={styles.calculoLabel}>Duración:</span>
                <span style={styles.calculoValue}>{dias} día{dias !== 1 ? 's' : ''}</span>
              </div>
              <div style={styles.calculoRow}>
                <span style={styles.calculoLabel}>Tarifa diaria:</span>
                <span style={styles.calculoValue}>USD ${parseFloat(precioDialo).toLocaleString('en-US', {minimumFractionDigits:2})}</span>
              </div>
              <div style={styles.calculoDivider}></div>
              <div style={styles.calculoRow}>
                <span style={styles.calculoLabel}>Subtotal:</span>
                <span style={styles.calculoValue}>USD ${parseFloat(subtotal).toLocaleString('en-US', {minimumFractionDigits:2})}</span>
              </div>
            </div>
          )}
        </div>

        {/* Sección 3: Costos */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>💰 Información Financiera</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Depósito de Garantía (USD)</label>
              <input
                type="number"
                style={styles.input}
                value={form.deposito}
                onChange={(e) => setForm({ ...form, deposito: e.target.value })}
                placeholder="0.00"
                min="0"
                step="100"
              />
              <span style={styles.inputHint}>
                Opcional - Se devolverá al finalizar la renta
              </span>
            </div>

            {dias > 0 && (
              <div style={styles.totalCard}>
                <div style={styles.totalLabel}>TOTAL A PAGAR</div>
                <div style={styles.totalAmount}>USD ${parseFloat(totalPagar).toLocaleString('en-US', {minimumFractionDigits:2})}</div>
                <div style={styles.totalBreakdown}>
                  Renta ({dias} día{dias !== 1 ? 's' : ''}) + Depósito
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sección 4: Observaciones */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📋 Notas y Observaciones</h3>
          <textarea
            style={styles.textarea}
            value={form.observaciones}
            onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
            placeholder="Detalles adicionales sobre el contrato, acuerdos especiales, etc."
            rows="4"
          />
        </div>

        {/* Botones de Acción */}
        <div style={styles.formActions}>
          <button
            style={styles.btnSecondary}
            onClick={() => navigate("/rencar")}
            disabled={loading}
          >
            ← Cancelar
          </button>
          <button
            style={styles.btnPrimary}
            onClick={crearRenta}
            disabled={loading || dias <= 0}
          >
            {loading ? (
              <div style={styles.btnLoading}>
                <div style={styles.btnSpinner}></div>
                <span>Creando contrato...</span>
              </div>
            ) : (
              <>
                <span style={styles.btnIcon}>✓</span>
                <span>Crear Contrato</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Animaciones */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: "calc(100vh - 70px)",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    padding: "24px 32px",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },

  // Page Header
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px"
  },
  pageTitle: {
    display: "flex",
    alignItems: "center",
    gap: "16px"
  },
  pageTitleIcon: {
    fontSize: "36px",
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    padding: "14px",
    borderRadius: "16px",
    border: "2px solid #93c5fd"
  },
  pageTitleText: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#1e293b",
    margin: 0
  },
  pageTitleSub: {
    fontSize: "14px",
    color: "#64748b",
    margin: "4px 0 0 0"
  },
  pageStats: {
    display: "flex",
    gap: "12px"
  },
  statBadge: {
    padding: "10px 16px",
    background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
    color: "#065f46",
    borderRadius: "12px",
    fontWeight: "700",
    fontSize: "14px",
    border: "2px solid #6ee7b7"
  },

  // Form Card
  formCard: {
    background: "white",
    borderRadius: "20px",
    padding: "32px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
    maxWidth: "1000px",
    margin: "0 auto"
  },

  // Sections
  section: {
    marginBottom: "32px",
    paddingBottom: "24px",
    borderBottom: "1px solid #f1f5f9"
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },

  // Form Elements
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px"
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#475569"
  },
  input: {
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    outline: "none",
    background: "#f8fafc",
    transition: "all 0.2s",
    fontFamily: "inherit"
  },
  inputHint: {
    fontSize: "12px",
    color: "#94a3b8",
    fontStyle: "italic"
  },
  textarea: {
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    outline: "none",
    background: "#f8fafc",
    resize: "vertical",
    fontFamily: "inherit",
    transition: "all 0.2s"
  },

  // Cálculo Card
  calculoCard: {
    marginTop: "20px",
    padding: "20px",
    background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
    borderRadius: "12px",
    border: "2px solid #93c5fd"
  },
  calculoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px"
  },
  calculoLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#475569"
  },
  calculoValue: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e40af"
  },
  calculoDivider: {
    height: "1px",
    background: "#93c5fd",
    margin: "12px 0"
  },

  // Total Card
  totalCard: {
    padding: "24px",
    background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
    borderRadius: "16px",
    border: "2px solid #6ee7b7",
    textAlign: "center"
  },
  totalLabel: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#065f46",
    letterSpacing: "0.5px",
    marginBottom: "8px"
  },
  totalAmount: {
    fontSize: "32px",
    fontWeight: "800",
    color: "#065f46",
    marginBottom: "4px"
  },
  totalBreakdown: {
    fontSize: "12px",
    color: "#059669"
  },

  // Buttons
  formActions: {
    display: "flex",
    gap: "16px",
    justifyContent: "flex-end",
    marginTop: "32px",
    paddingTop: "24px",
    borderTop: "2px solid #f1f5f9"
  },
  btnPrimary: {
    padding: "14px 32px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s"
  },
  btnSecondary: {
    padding: "14px 32px",
    background: "#f1f5f9",
    color: "#475569",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s"
  },
  btnIcon: {
    fontSize: "18px"
  },
  btnLoading: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  btnSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  }
};

export default RentaCrear;